import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Typography,
  Box
} from '@mui/material';
import type { Rol, Permiso } from '../types/usuario.types';
import { rolService } from '../services/rolService';
import { permisoService } from '../services/permisoService';

interface AsignarPermisosDialogProps {
  open: boolean;
  onClose: () => void;
  rol?: Rol;
}

export const AsignarPermisosDialog: React.FC<AsignarPermisosDialogProps> = ({
  open,
  onClose,
  rol
}) => {
  const [permisosDisponibles, setPermisosDisponibles] = useState<Permiso[]>([]);
  const [permisosSeleccionados, setPermisosSeleccionados] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && rol) {
      loadData();
    }
  }, [open, rol]);

  const loadData = async () => {
    if (!rol) return;

    setLoading(true);
    setError(null);

    try {
      // Cargar todos los permisos disponibles
      const todosPermisos = await permisoService.getAll();
      setPermisosDisponibles(todosPermisos);

      // Cargar permisos actuales del rol
      const permisosActuales = await rolService.getPermisos(rol.id);
      setPermisosSeleccionados(permisosActuales.map(p => p.id));
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar los permisos');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermiso = (idPermiso: number) => {
    setPermisosSeleccionados(prev => {
      if (prev.includes(idPermiso)) {
        return prev.filter(id => id !== idPermiso);
      } else {
        return [...prev, idPermiso];
      }
    });
  };

  const handleSelectAll = () => {
    if (permisosSeleccionados.length === permisosDisponibles.length) {
      setPermisosSeleccionados([]);
    } else {
      setPermisosSeleccionados(permisosDisponibles.map(p => p.id));
    }
  };

  const handleSave = async () => {
    if (!rol) return;

    setSaving(true);
    setError(null);

    try {
      await rolService.asignarPermisos(rol.id, permisosSeleccionados);
      onClose();
    } catch (err) {
      console.error('Error saving permissions:', err);
      setError('Error al asignar permisos');
    } finally {
      setSaving(false);
    }
  };

  const allSelected = permisosSeleccionados.length === permisosDisponibles.length && permisosDisponibles.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Asignar Permisos a Rol: {rol?.descripcion}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Selecciona los permisos que deseas asignar a este rol
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={permisosSeleccionados.length > 0 && !allSelected}
                  onChange={handleSelectAll}
                />
              }
              label={<strong>Seleccionar todos</strong>}
              sx={{ mb: 2 }}
            />

            <FormGroup>
              {permisosDisponibles.map((permiso) => (
                <FormControlLabel
                  key={permiso.id}
                  control={
                    <Checkbox
                      checked={permisosSeleccionados.includes(permiso.id)}
                      onChange={() => handleTogglePermiso(permiso.id)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">{permiso.descripcion}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Código: {permiso.codigo}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>

            {permisosDisponibles.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No hay permisos disponibles
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AsignarPermisosDialog;
